import * as React from "react";
import styled from "styled-components";
import DynamicFactory, {
  Button,
  Label,
  LinearLayout,
  View,
  getStructureValues,
  updateStructureValues,
} from "../DynamicFactory";
import defaultStructure from "./structure";
import mock from "./mock";
import {
  formatAddress,
  formatAllHours,
  getAllHours,
  getTodaysHours,
} from "./utils";
import merge from "../helpers/merge";
import { DynamicStructureType } from "../types";
import { ComponentProps, ComponentStyle } from "./types";

export const defaultConfig = {
  showAddress: true,
  showPhoneNumber: true,
  showWaitTime: true,
  showStoreHours: true,
  showPickUpHours: true,
  showDeliveryHours: true,
};

export const defaultStyles: ComponentStyle = {
  primaryButtonBackgroundColor: `#feed01`,
  primaryButtonTextColor: `#3d3d3d`,
  secondaryButtonBackgroundColor: `#ffffff`,
  secondaryButtonTextColor: `#666666`,
  buttonStyle: `rounded`,
};

// Source dictionary for component style
const dictionary = {
  primaryButtonBackgroundColor: `buttons.LocationCard_OrderNowButton.fillColor`,
  primaryButtonTextColor: `labels.LocationCard_OrderNowButtonLabel_unselected.fillColor`,
  secondaryButtonBackgroundColor: `buttons.LocationCard_ScheduleForLaterButton.fillColor`,
  secondaryButtonTextColor: `labels.LocationCard_ScheduleForLaterButtonLabel_unselected.fillColor`,
};

// Target dictionary for component style
const updateDictionary = {
  primaryButtonBackgroundColor: [
    `buttons.LocationCard_OrderNowButton.fillColor`,
    `buttons.LocationCard_PreorderButton.fillColor`,
  ],
  primaryButtonTextColor: [
    `buttons.LocationCard_OrderNowButton.label.fillColor`,
    `buttons.LocationCard_PreorderButton.label.fillColor`,
  ],
  secondaryButtonBackgroundColor: [
    `buttons.LocationCard_ScheduleForLaterButton.fillColor`,
  ],
  secondaryButtonTextColor: [
    `buttons.LocationCard_ScheduleForLaterButton.label.fillColor`,
  ],
};

const MarketplaceIcon = styled.img`
  max-width: 3rem;
  max-height: 3rem;
  border-radius: 100%;
  border: 1px solid #e0e0e0;
  margin-right: 0.75rem;
`;

const populatedStates = (
  structure: DynamicStructureType,
  sx: Partial<Record<keyof typeof defaultStyles, string>> = {},
) => {
  const construct = merge(defaultStructure, structure);
  const newStyle = {
    ...defaultStyles,
    ...getStructureValues(dictionary, construct),
    ...sx,
  };
  // TODO: Remove update structure values, use Button sx prop instead
  const newStructure = updateStructureValues<typeof updateDictionary>(
    updateDictionary,
    construct,
    sx,
  );

  return {
    sx: newStyle,
    structure: newStructure,
  } as {
    structure: DynamicStructureType;
    sx: ComponentStyle;
  };
};

/**
 * Renders a single location card.
 *
 * Behaviors:
 * - Daily and store hours can be toggled if they're not
 *   the same for each day.
 * - The "button" (actually a link) for the card has text
 *   that depends on whether the store is open or not.
 * - If a store is closed, the button isn't clickable.
 * - The card receives a `data-locationid` element so it
 *   can be targetted by map events for scrolling without
 *   having to manage a bunch of messy refs.
 */
function LocationCard({
  config = {},
  location = mock.location,
  // onClick,
  onOrderClick,
  onPreorderClick,
  onScheduleClick,
  onMarketplaceClick,
  onUpdate,
  orderType = `pickup`,
  structure = {},
  sx = defaultStyles,
}: ComponentProps<keyof typeof updateDictionary>) {
  const {
    primaryButtonBackgroundColor,
    primaryButtonTextColor,
    secondaryButtonBackgroundColor,
    secondaryButtonTextColor,
    buttonStyle,
  } = sx;
  const { sx: style, structure: dynamicStructure } = React.useMemo(
    () =>
      populatedStates(structure as DynamicStructureType, {
        ...(primaryButtonBackgroundColor !== `#feed01` && {
          primaryButtonBackgroundColor,
        }),
        ...(primaryButtonTextColor !== `#3d3d3d` && {
          primaryButtonTextColor,
        }),
        ...(secondaryButtonBackgroundColor !== `#ffffff` && {
          secondaryButtonBackgroundColor,
        }),
        ...(secondaryButtonTextColor !== `#666666` && {
          secondaryButtonTextColor,
        }),
        buttonStyle,
      }),
    [sx, structure],
  );

  const [toogleStoreButton, setToggleStoreButton] = React.useState(false);
  const [tooglePickupButton, setTogglePickupButton] = React.useState(false);
  const [toogleDeliveryButton, setToggleDeliveryButton] = React.useState(false);

  const hours = location?.hours || [];
  // const allDailyHours = getAllHours(hours, orderType);
  const allStoreHours = getAllHours(hours, `kiosk`);
  const allPickupHours = getAllHours(hours, `pickup`);
  const allDeliveryHours = getAllHours(hours, `delivery`);

  const canStoreHoursBeExpanded = allStoreHours.length > 1;
  const canPickupHoursBeExpanded = allPickupHours.length > 1;
  const canDeliveryHoursBeExpanded = allDeliveryHours.length > 1;

  const {
    showAddress,
    showPhoneNumber,
    showWaitTime,
    showStoreHours,
    showPickUpHours,
    showDeliveryHours,
  } = { ...defaultConfig, ...config };

  const isDistanceNumber =
    location?.distanceInMiles && typeof location?.distanceInMiles === `number`;

  React.useEffect(() => {
    if (onUpdate) {
      onUpdate({
        structure: dynamicStructure,
        sx: style,
        config: {
          ...defaultConfig,
          ...config,
        },
      });
    }
  }, []);

  return (
    <DynamicFactory<typeof defaultStructure>
      structure={dynamicStructure}
      entry="LocationCard"
      dynamicValues={{
        LocationCard_LocationLabel: [location?.name || ``],
        LocationCard_AddressLabel: [formatAddress(location?.address)],
        LocationCard_DistanceLabel: [
          (isDistanceNumber && location?.distanceInMiles?.toFixed(1)) || ``,
        ],
        LocationCard_TimeLabel: [location?.estimatedTime || ``],
        LocationCard_PhoneLabel: [location?.phoneNumber || ``],
        LocationCard_StoreOpenNowLabel: [
          toogleStoreButton ? `` : `: ${getTodaysHours(hours, `kiosk`, false)}`,
        ],
        LocationCard_PickupOpenNowLabel: [
          tooglePickupButton
            ? ``
            : `: ${getTodaysHours(hours, `pickup`, false)}`,
        ],
        LocationCard_DeliveryOpenNowLabel: [
          toogleDeliveryButton
            ? ``
            : `: ${getTodaysHours(hours, `delivery`, false)}`,
        ],
        LocationCard_SeeAllRestaurantsLabel: [
          `${location?.restaurantGroups?.length || `0`}`,
        ],
      }}
      renderers={{
        LocationCard: (p) => (
          <View
            data-locationid={location?.id}
            // disabled={!location?.isOpen}
            // {...(onClick && { onClick })}
            {...p}
          />
        ),
        LocationCard_DistanceWrapperLayout: (p) =>
          (isDistanceNumber || (showWaitTime && location?.estimatedTime)) && (
            <LinearLayout {...p} />
          ),
        LocationCard_PhoneAddressWrapper: (p) =>
          (showAddress || showPhoneNumber) && <View {...p} />,
        LocationCard_AddressWrapper: (p) => showAddress && <View {...p} />,
        LocationCard_PhoneWrapper: (p) =>
          showPhoneNumber && location?.phoneNumber && <View {...p} />,

        // Labels
        LocationCard_LocationLabel: (p) => <Label {...p} as="h2" />,
        LocationCard_TimeLabel: (p) =>
          showWaitTime && location?.estimatedTime && <Label {...p} />,
        LocationCard_DistanceLabel: (p) => isDistanceNumber && <Label {...p} />,
        LocationCard_SeparatorLabel: (p) =>
          isDistanceNumber && <Label {...p} />,

        // Icons
        LocationCard_AddressIcon: (p) => (
          <View role="img" aria-label="Address" {...p} />
        ),
        LocationCard_PhoneIcon: (p) => (
          <View role="img" aria-label="Phone Number" {...p} />
        ),

        // Action buttons
        LocationCard_OrderNowButton: (p) =>
          typeof onOrderClick === `function` && (
            <Button
              {...p}
              sx={{ buttonStyle: style.buttonStyle }}
              onClick={onOrderClick}
              disabled={!location?.isOpen}
            />
          ),

        LocationCard_PreorderButton: (p) =>
          typeof onPreorderClick === `function` && (
            <Button
              {...p}
              sx={{ buttonStyle: style.buttonStyle }}
              onClick={onPreorderClick}
            />
          ),

        LocationCard_ScheduleForLaterButton: (p) =>
          typeof onScheduleClick === `function` && (
            <Button
              {...p}
              sx={{ buttonStyle: style.buttonStyle }}
              onClick={onScheduleClick}
            />
          ),

        // Store Hours
        LocationCard_StoreHours: (p) => showStoreHours && <View {...p} />,
        LocationCard_StoreHoursLabel: (p) => <Label {...p} as="h3" />,
        LocationCard_StoreHoursHeaderWrapper: (p) => (
          <View {...p} {...canStoreHoursBeExpanded} />
        ),
        LocationCard_StoreHoursChevronButton: (p) =>
          canStoreHoursBeExpanded && (
            <Button
              {...p}
              selected={toogleStoreButton}
              aria-expanded={toogleStoreButton}
              ariaLabel="Store Hours"
              onClick={() => setToggleStoreButton((prev) => !prev)}
              onKeyDown={(e: KeyboardEvent): void => {
                if (e.key === ` ` || e.key === `Enter`) {
                  setToggleStoreButton((prev) => !prev);
                }
              }}
            />
          ),
        LocationCard_StoreHoursCollectionView: () =>
          toogleStoreButton && (
            <Label name="LocationCard_DayLabel">
              {formatAllHours(allStoreHours)}
            </Label>
          ),

        // Pickup Hours
        LocationCard_PickupHours: (p) =>
          showPickUpHours && orderType === `pickup` && <View {...p} />,
        LocationCard_PickupHoursLabel: (p) => <Label {...p} as="h3" />,
        LocationCard_PickupHoursHeaderWrapper: (p) => (
          <View {...p} {...canPickupHoursBeExpanded} />
        ),
        LocationCard_PickupHoursChevronButton: (p) =>
          canPickupHoursBeExpanded && (
            <Button
              {...p}
              selected={tooglePickupButton}
              aria-expanded={tooglePickupButton}
              ariaLabel="Pickup Hours"
              onClick={() => setTogglePickupButton((prev) => !prev)}
              onKeyDown={(e: KeyboardEvent): void => {
                if (e.key === ` ` || e.key === `Enter`) {
                  setTogglePickupButton((prev) => !prev);
                }
              }}
            />
          ),
        LocationCard_PickupHoursCollectionView: () =>
          tooglePickupButton && (
            <Label name="LocationCard_DayLabel">
              {formatAllHours(allPickupHours)}
            </Label>
          ),

        // Delivery Hours
        LocationCard_DeliveryHours: (p) =>
          showDeliveryHours && orderType === `delivery` && <View {...p} />,
        LocationCard_DeliveryHoursLabel: (p) => <Label {...p} as="h3" />,
        LocationCard_DeliveryHoursHeaderWrapper: (p) => (
          <View {...p} {...canDeliveryHoursBeExpanded} />
        ),
        LocationCard_DeliveryHoursChevronButton: (p) =>
          canDeliveryHoursBeExpanded && (
            <Button
              {...p}
              selected={toogleDeliveryButton}
              aria-expanded={toogleDeliveryButton}
              ariaLabel="Delivery Hours"
              onClick={() => setToggleDeliveryButton((prev) => !prev)}
              onKeyDown={(e: KeyboardEvent): void => {
                if (e.key === ` ` || e.key === `Enter`) {
                  setToggleDeliveryButton((prev) => !prev);
                }
              }}
            />
          ),
        LocationCard_DeliveryHoursCollectionView: () =>
          toogleDeliveryButton && (
            <Label name="LocationCard_DayLabel">
              {formatAllHours(allDeliveryHours)}
            </Label>
          ),

        // Marketplace - hiding until we have more design and tests
        LocationCard_SeeAllRestaurantsWrapper: (p) =>
          location?.restaurantGroups &&
          Boolean(location?.restaurantGroups?.length) &&
          location?.restaurantGroups?.length > 7 && (
            <View {...p} onClick={onMarketplaceClick} />
          ),
        LocationCard_MarketplaceIconsWrapper: (p) =>
          location?.restaurantGroups &&
          Boolean(location?.restaurantGroups?.length) &&
          location?.restaurantGroups?.length <= 7 && <View {...p} />,
        LocationCard_MarketplaceIconsCollectionView: () => (
          <div style={{ display: `flex` }}>
            {location?.restaurantGroups?.map(({ id, media, name }) => (
              <MarketplaceIcon
                key={id}
                src={media?.restaurantIcon}
                alt={name}
              />
            ))}
          </div>
        ),
      }}
    />
  );
}

LocationCard.defaultProps = {
  config: defaultConfig,
  location: mock.location,
  onClick: undefined,
  onOrderClick: undefined,
  onPreorderClick: undefined,
  onScheduleClick: undefined,
  onUpdate: undefined,
  orderType: `pickup`,
  structure: defaultStructure,
  sx: defaultStyles,
};

export default LocationCard;
export { default as mock } from "./mock";
export { default as defaultStructure } from "./structure";
export * from "./types";
export * from "./utils";
